import React from 'react';
import { Link } from 'react-router-dom';
import { useWishlist } from '../Context/WishlistContext';
import { useCart } from '../Context/CartContext';
import { useLanguage, getLanguageText } from '../Context/LanguageContext';
import { getActiveDiscounts, getCategories } from '../firebase/services';
import { getBestDiscountForProduct, computeDiscountedPrice, formatDiscountBadge } from '../Utils/discounts';
import SEO from '../Components/SEO';

export default function Wishlist() {
  const { wishlistItems, removeFromWishlist, clearWishlist } = useWishlist();
  const { addToCart } = useCart();
  const { language } = useLanguage();

  const [activeDiscounts, setActiveDiscounts] = React.useState([]);
  const [categories, setCategories] = React.useState([]);

  React.useEffect(() => {
    let mounted = true;
    Promise.all([getActiveDiscounts(), getCategories()])
      .then(([discounts, cats]) => {
        if (!mounted) return;
        setActiveDiscounts(Array.isArray(discounts) ? discounts : []);
        setCategories(Array.isArray(cats) ? cats : []);
      })
      .catch(() => {
        if (!mounted) return;
        setActiveDiscounts([]);
        setCategories([]);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const toSlug = React.useCallback((value, fallback = 'uncategorized') => {
    const raw = (value ?? '').toString();
    const firstPart = raw.split(' / ')[0].trim().toLowerCase();
    const cleaned = firstPart
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9\u0600-\u06FF-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
    return cleaned || fallback;
  }, []);

  const getProductLink = React.useCallback((product) => {
    const rawPrimary = (product?.categories && Array.isArray(product.categories) && product.categories.length > 0)
      ? product.categories[0]
      : (product?.categoryId || product?.category);

    const resolved = categories.find(c => c?.id === rawPrimary) || null;
    const label = resolved
      ? (resolved.nameEn || resolved.name || resolved.nameAr)
      : (typeof rawPrimary === 'string' ? rawPrimary : 'uncategorized');

    const categorySlug = toSlug(label, 'uncategorized');

    let name = '';
    if (product?.nameEn) name = product.nameEn;
    else if (typeof product?.name === 'string') name = product.name;
    else if (typeof product?.name === 'object' && product.name !== null) name = product.name.en || product.name.ar || '';
    else if (product?.nameAr) name = product.nameAr;

    const firstPart = (name || '').split(' / ')[0];
    const productSlug = firstPart.toLowerCase().replace(/\s+/g, '-');

    return `/products/${categorySlug}/${productSlug}`;
  }, [categories, toSlug]);

  const getBasePrice = (product) => {
    if (product?.tastes && Array.isArray(product.tastes) && product.tastes.length > 0) {
      const first = product.tastes[0];
      const p = Number(first?.price ?? first ?? 0);
      return Number.isFinite(p) ? p : 0;
    }
    if (product?.tastePrices && Array.isArray(product.tastePrices) && product.tastePrices.length > 0) {
      const p = Number(product.tastePrices[0] ?? 0);
      return Number.isFinite(p) ? p : 0;
    }
    const p = Number(product?.price ?? 0);
    return Number.isFinite(p) ? p : 0;
  };

  const wishlistSeo = (
    <SEO
      title="Wishlist"
      description="Your saved items - VetCare Store"
      canonical={typeof window !== 'undefined' ? `${window.location.origin}/wishlist` : ''}
      type="website"
    />
  );

  return (
    <div className="container" style={{ padding: '40px 20px 60px' }}>
      {wishlistSeo}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Wishlist</h2>
        {wishlistItems.length > 0 && (
          <button
            className="btn"
            onClick={clearWishlist}
            style={{ background: '#fff', border: '1px solid #eee', color: '#333', padding: '10px 14px', borderRadius: '8px', cursor: 'pointer' }}
          >
            Clear
          </button>
        )}
      </div>

      {wishlistItems.length === 0 ? (
        <div style={{ padding: '30px 0', color: '#666' }}>
          <p style={{ marginTop: 10 }}>Your wishlist is empty.</p>
          <Link to="/shop" className="btn">Go to Shop</Link>
        </div>
      ) : (
        <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {wishlistItems.map(item => {
            const name = getLanguageText(item.name || { en: item.nameEn || '', ar: item.nameAr || '' }, language) || 'Unnamed Product';
            const image = (item.imageUrls && item.imageUrls[0]) || (item.images && item.images[0]) || item.imageUrl || 'https://placehold.co/80x80';

            const basePrice = getBasePrice(item);
            const bestDiscount = getBestDiscountForProduct(item, activeDiscounts);
            const pricing = computeDiscountedPrice(basePrice, bestDiscount);
            const badge = formatDiscountBadge(bestDiscount);
            const hasDiscount = pricing.finalPrice < pricing.basePrice;

            return (
              <div
                key={item.wishlistId || item.id}
                style={{
                  display: 'flex',
                  gap: '14px',
                  alignItems: 'center',
                  padding: '12px',
                  border: '1px solid #eee',
                  borderRadius: '10px',
                  background: '#fff',
                  flexWrap: 'wrap'
                }}
              >
                <Link to={getProductLink(item)} style={{ display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none', flex: '1 1 320px' }}>
                  <img
                    src={image}
                    alt={name}
                    style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 10, border: '1px solid #f3f4f6' }}
                    onError={(e) => { e.target.src = 'https://placehold.co/80x80?text=No+Image'; }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 800, color: '#1B92A8', lineHeight: 1.2 }}>{name}</div>
                    {hasDiscount ? (
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'baseline', flexWrap: 'wrap', marginTop: 6 }}>
                        <span style={{ color: '#6b7280', textDecoration: 'line-through', fontWeight: 700 }}>
                          {pricing.basePrice} EGP
                        </span>
                        <span style={{ color: '#1B92A8', fontWeight: 900 }}>
                          {pricing.finalPrice} EGP
                        </span>
                        <span style={{ background: '#ff4d4f', color: '#fff', fontWeight: 800, fontSize: '0.75rem', padding: '2px 8px', borderRadius: '9999px' }}>
                          {badge || 'OFF'}
                        </span>
                      </div>
                    ) : (
                      <div style={{ marginTop: 6, color: '#1B92A8', fontWeight: 900 }}>
                        {basePrice > 0 ? `${basePrice} EGP` : 'Price on request'}
                      </div>
                    )}
                  </div>
                </Link>

                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <button
                    onClick={() => {
                      // Add first taste/price similar to ProductCard
                      let selectedTaste = null;
                      let selectedPrice = 0;

                      if (item.tastes && Array.isArray(item.tastes) && item.tastes.length > 0) {
                        selectedTaste = item.tastes[0];
                        selectedPrice = item.tastes[0].price || item.tastes[0] || 0;
                      } else if (item.tastePrices && Array.isArray(item.tastePrices) && item.tastePrices.length > 0) {
                        selectedPrice = item.tastePrices[0];
                      } else if (item.price) {
                        selectedPrice = item.price;
                      }

                      const productToAdd = {
                        ...item,
                        baseProductId: item.baseProductId || item.id,
                        basePrice: selectedPrice,
                        selectedPrice,
                        price: selectedPrice,
                        selectedTaste,
                        id: selectedTaste ? `${item.id}_${selectedTaste.name || 'taste0'}` : item.id
                      };

                      addToCart(productToAdd);
                    }}
                    className="btn"
                    style={{ padding: '10px 14px' }}
                  >
                    Add to Cart
                  </button>

                  <button
                    aria-label="Remove"
                    onClick={() => removeFromWishlist(item)}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      border: '1px solid #eee',
                      background: '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <ion-icon name="trash-outline" style={{ fontSize: 20, color: '#ff4d4f' }}></ion-icon>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
